import { useState, useEffect } from "react";
import { IoMdCheckmark } from "react-icons/io";
import "react-datepicker/dist/react-datepicker.css";
import styles from "../../styles/__labDialogAddEditTest.module.scss";
import DatePicker from "react-datepicker";
import axios from "axios";
import { APP_PATH } from "../../api/backendPath";
import { toast } from '../toast';

export default function PhysioDialogRevisit({
  open,
  onClose,
  selectedAppointment,
  onSuccess,
}) {
  const [date, setDate] = useState(null);
  const [timeSlot, setTimeSlot] = useState("");
  const [serviceTime, setServiceTime] = useState("");
  const [saving, setSaving] = useState(false);

  const timeSlots = [
    { value: "MORNING", label: "Morning (8:00 AM - 12:00 PM)", min: "08:00", max: "12:00" },
    { value: "AFTERNOON", label: "Afternoon (12:00 PM - 5:00 PM)", min: "12:00", max: "17:00" },
    { value: "EVENING", label: "Evening (5:00 PM - 7:30 PM)", min: "17:00", max: "19:30" },
  ];

  // Generate time options based on selected slot
  const getTimeOptions = () => {
    const slot = timeSlots.find(s => s.value === timeSlot);
    if (!slot) return [];
    const options = [];
    const [minH, minM] = slot.min.split(':').map(Number);
    const [maxH, maxM] = slot.max.split(':').map(Number);
    for (let h = minH; h <= maxH; h++) {
      for (let m = 0; m < 60; m += 30) {
        if (h === minH && m < minM) continue;
        if (h === maxH && m > maxM) break;
        if (h > maxH) break;
        const val = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        const dh = h === 0 ? 12 : h > 12 ? h - 12 : h;
        const period = h >= 12 ? 'PM' : 'AM';
        options.push({ value: val, label: `${dh}:${String(m).padStart(2, '0')} ${period}` });
      }
    }
    return options;
  };

  useEffect(() => {
    if (open) {
      document.body.classList.add("modal-open");
      setDate(null);
      setTimeSlot("");
      setServiceTime("");
    } else {
      document.body.classList.remove("modal-open");
    }
    return () => document.body.classList.remove("modal-open");
  }, [open]);

  if (!open) return null;

  const handleSubmit = async () => {
    if (!date) { toast.error("Please select a date"); return; }
    if (!timeSlot) { toast.error("Please select a time slot"); return; }
    if (!serviceTime) { toast.error("Please select a time"); return; }

    const orderId = selectedAppointment?.raw?.id || selectedAppointment?.id;
    if (!orderId || isNaN(Number(orderId))) { toast.error("No order selected"); return; }

    setSaving(true);
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const axiosInstance = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        headers: { Authorization: `Bearer ${token}` },
      });

      const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

      const response = await axiosInstance.post(`${APP_PATH.physioOrders}/${orderId}/revisit-request`, {
        scheduledDate: formattedDate,
        preferredTimeSlot: timeSlot,
        scheduledTime: serviceTime,
      });

      if (response?.data?.status) {
        toast.success("Revisit request sent successfully");
        onSuccess?.();
        onClose();
      } else {
        toast.error(response?.data?.message || "Failed to send revisit request");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to send revisit request");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`${styles["labDialog_wrapper"]}`}>
      <div className={`${styles["dialog-overlay"]}`}>
        <div className={styles["dialog-box"]}>
          {/* Close Button */}
          <button className={styles["dialog-close"]} onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M13.5525 12.0002L17.8525 7.71019C18.0408 7.52188 18.1466 7.26649 18.1466 7.00019C18.1466 6.73388 18.0408 6.47849 17.8525 6.29019C17.6642 6.10188 17.4088 5.99609 17.1425 5.99609C16.8762 5.99609 16.6208 6.10188 16.4325 6.29019L12.1425 10.5902L7.85252 6.29019C7.66421 6.10188 7.40882 5.99609 7.14252 5.99609C6.87622 5.99609 6.62082 6.10188 6.43252 6.29019C6.24421 6.47849 6.13843 6.73388 6.13843 7.00019C6.13843 7.26649 6.24421 7.52188 6.43252 7.71019L10.7325 12.0002L6.43252 16.2902C6.33879 16.3831 6.26439 16.4937 6.21363 16.6156C6.16286 16.7375 6.13672 16.8682 6.13672 17.0002C6.13672 17.1322 6.16286 17.2629 6.21363 17.3848C6.26439 17.5066 6.33879 17.6172 6.43252 17.7102C6.52548 17.8039 6.63608 17.8783 6.75794 17.9291C6.8798 17.9798 7.01051 18.006 7.14252 18.006C7.27453 18.006 7.40523 17.9798 7.52709 17.9291C7.64895 17.8783 7.75955 17.8039 7.85252 17.7102L12.1425 13.4102L16.4325 17.7102C16.5255 17.8039 16.6361 17.8783 16.7579 17.9291C16.8798 17.9798 17.0105 18.006 17.1425 18.006C17.2745 18.006 17.4052 17.9798 17.5271 17.9291C17.649 17.8783 17.7596 17.8039 17.8525 17.7102C17.9462 17.6172 18.0206 17.5066 18.0714 17.3848C18.1222 17.2629 18.1483 17.1322 18.1483 17.0002C18.1483 16.8682 18.1222 16.7375 18.0714 16.6156C18.0206 16.4937 17.9462 16.3831 17.8525 16.2902L13.5525 12.0002Z" fill="currentcolor"/>
            </svg>
          </button>

          {/* Title */}
          <div className={styles["mb-24"]}>
            <h2 className={styles["dialog-title"]}>Revisit Request</h2>
          </div>

          <div className={styles["form-design"]}>
            {/* Service Date */}
            <div className={styles["form-group"]}>
              <label>Service Date</label>
              <DatePicker
                className={`${styles["apt-date-filter"]} ${styles["date-select"]}`}
                selected={date}
                onChange={(d) => setDate(d)}
                placeholderText="Select date"
                dateFormat="dd/MM/yyyy"
                minDate={new Date()}
              />
            </div>

            {/* Time Slot - Radio buttons */}
            <div className={styles["form-group"]}>
              <label>Preferred Time Slot</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '6px' }}>
                {timeSlots.map((slot) => (
                  <label key={slot.value} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', color: '#374151' }}>
                    <input
                      type="radio"
                      name="timeSlot"
                      value={slot.value}
                      checked={timeSlot === slot.value}
                      onChange={(e) => { setTimeSlot(e.target.value); setServiceTime(""); }}
                      style={{ accentColor: '#099494', width: '16px', height: '16px' }}
                    />
                    {slot.label}
                  </label>
                ))}
              </div>
            </div>

            {/* Service Time - Dropdown based on slot */}
            {timeSlot && (
              <div className={styles["form-group"]}>
                <label>Service Time</label>
                <select
                  className={styles["select-input"]}
                  value={serviceTime}
                  onChange={(e) => setServiceTime(e.target.value)}
                >
                  <option value="">Select time</option>
                  {getTimeOptions().map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className={`${styles["dialog-footer"]}`}>
            <button className={`${styles["btn-outlined-1"]}`} onClick={onClose}>
              Cancel
            </button>
            <button
              className={styles["btn-1"]}
              onClick={handleSubmit}
              disabled={saving || !date || !timeSlot || !serviceTime}
              style={{ opacity: (saving || !date || !timeSlot || !serviceTime) ? 0.6 : 1 }}
            >
              <IoMdCheckmark /> {saving ? 'Sending...' : 'Send Revisit Request'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
